import React, { useRef, useEffect } from 'react';
import type { ChartData } from '../../types/analytics';

interface Props {
  data: ChartData;
  title: string;
  size?: number;
  showLegend?: boolean;
  showPercentages?: boolean;
}

export default function PieChart({ 
  data, 
  title, 
  size = 300, 
  showLegend = true, 
  showPercentages = true 
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current || !data.datasets.length) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = size * window.devicePixelRatio;
    canvas.height = size * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    // Clear canvas
    ctx.clearRect(0, 0, size, size);

    const centerX = size / 2;
    const centerY = size / 2;
    const radius = Math.min(centerX, centerY) - 20;

    const dataset = data.datasets[0];
    const values = dataset.data;
    const total = values.reduce((sum, value) => sum + value, 0);

    if (total === 0) return;

    const colors = [
      '#003049',
      '#D62828',
      '#F77F00',
      '#FCBF49',
      '#EAE2B7',
      '#8ECAE6',
      '#219EBC',
      '#023047',
      '#FFB3BA',
      '#BAFFC9'
    ];

    let currentAngle = -Math.PI / 2; // Start from top

    values.forEach((value, index) => {
      const sliceAngle = (value / total) * 2 * Math.PI;
      const color = colors[index % colors.length];

      // Draw slice
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, currentAngle, currentAngle + sliceAngle);
      ctx.closePath();
      ctx.fill();

      // Draw percentage label
      if (showPercentages && value > 0) {
        const percentage = ((value / total) * 100).toFixed(1);
        const labelAngle = currentAngle + sliceAngle / 2;
        const labelX = centerX + Math.cos(labelAngle) * (radius * 0.7);
        const labelY = centerY + Math.sin(labelAngle) * (radius * 0.7);

        ctx.fillStyle = 'white';
        ctx.font = 'bold 12px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`${percentage}%`, labelX, labelY);
      }

      currentAngle += sliceAngle;
    });

  }, [data, size, showPercentages]);

  return (
    <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-6">
      <h3 className="text-lg font-semibold text-white mb-4">{title}</h3>
      <div className="flex items-center justify-center space-x-6">
        <canvas
          ref={canvasRef}
          className="flex-shrink-0"
          style={{ width: `${size}px`, height: `${size}px` }}
        />
        
        {showLegend && (
          <div className="space-y-2">
            {data.labels.map((label, index) => {
              const colors = [
                '#003049', '#D62828', '#F77F00', '#FCBF49', '#EAE2B7',
                '#8ECAE6', '#219EBC', '#023047', '#FFB3BA', '#BAFFC9'
              ];
              const color = colors[index % colors.length];
              const value = data.datasets[0].data[index];
              const total = data.datasets[0].data.reduce((sum, v) => sum + v, 0);
              const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';

              return (
                <div key={index} className="flex items-center space-x-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-sm text-gray-300">
                    {label}: {percentage}%
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}